/**
 * Warms the browser cache for full-size media images, and finds the upscaled
 * copies that exist alongside most compound slides.
 *
 * Selecting an image does not update the store until its full-size JPG has
 * downloaded, and those run 140-290 KB and take a second or more from S3, so a
 * click can sit there with nothing happening. Starting the download as soon as
 * the pointer touches a thumbnail usually means it is already in hand by the
 * time the click lands.
 */
import Constants from '../enums/Constants';

const UPSCALED_SUFFIX = '_upscaled';

const requested = new Set();

function mediaUrl(media, suffix) {
  return `${Constants.S3_BUCKET_URL}${Constants.MEDIA_CONTENT}${media.mediaName}${suffix || ''}${Constants.IMG_EXT_JPG}`;
}

/**
 * Starts loading a media item's full-size image if it is not already loaded or
 * on its way. The loaded image is parked on the media object, which is the same
 * place ImageMixin looks before it fetches anything.
 */
export function prefetchMedia(media) {
  if (!media || media.type != 'static' || media.img || requested.has(media.id)) {
    return;
  }
  requested.add(media.id);
  const image = new Image();
  // Must match what ImageMixin asks for, or the two share no cache entry.
  image.crossOrigin = 'Anonymous';
  image.decoding = 'async';
  image.onload = () => {
    media.img = image;
  };
  image.onerror = () => {
    requested.delete(media.id);
  };
  image.src = mediaUrl(media);
}

/** Prefetches a handful of items, nearest first. */
export function prefetchAround(list, index, reach = 2) {
  if (!list || !list.length) {
    return;
  }
  for (let step = 1; step <= reach; step += 1) {
    prefetchMedia(list[index + step]);
    prefetchMedia(list[index - step]);
  }
}

/**
 * Resolves true when an upscaled copy of this slide exists.
 *
 * The bucket only allows GET cross-origin, so HEAD is not an option. This asks
 * for the file and drops the body the moment the status line comes back, which
 * costs no measurable transfer even though the file itself is a couple of MB.
 * The answer is cached on the media object.
 */
export function probeUpscaled(media) {
  if (!media || media.type != 'static') {
    return Promise.resolve(false);
  }
  if (media.hasUpscaled !== undefined) {
    return Promise.resolve(media.hasUpscaled);
  }
  if (!media.upscaledProbe) {
    media.upscaledProbe = fetch(mediaUrl(media, UPSCALED_SUFFIX))
      .then((response) => {
        if (response.body) {
          response.body.cancel().catch(() => { });
        }
        media.hasUpscaled = response.ok;
        return response.ok;
      })
      .catch(() => {
        media.hasUpscaled = false;
        return false;
      })
      .finally(() => {
        media.upscaledProbe = null;
      });
  }
  return media.upscaledProbe;
}

/**
 * Loads the upscaled copy onto the media object. Several MB, so this only runs
 * when something actually asks to show it.
 */
export function loadUpscaled(media) {
  if (!media || media.type != 'static' || media.imgUpscaled || media.upscaledLoading) {
    return;
  }
  media.upscaledLoading = true;
  const image = new Image();
  image.crossOrigin = 'Anonymous';
  image.decoding = 'async';
  image.onload = () => {
    media.imgUpscaled = image;
    media.upscaledLoading = false;
  };
  image.onerror = () => {
    media.upscaledLoading = false;
    media.hasUpscaled = false;
  };
  image.src = mediaUrl(media, UPSCALED_SUFFIX);
}
